import { useEffect } from 'react';
import { useAtom } from 'jotai';
import * as R from 'ramda';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import RankingRuleEditForm from 'components/RankingRule/RankingRuleEditForm';
import Loader from 'components/Loader';
import {
  rankingRuleTriggerModalAtom,
  rankingRuleTargetModalAtom,
} from '../../atoms/ranking';
import {
  RANKING_RULE_TRIGER_MODE,
  QUERY_KEYS,
  RANKING_RULE_TARGET_TYPES,
} from 'constants/index';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';

const groupByMode = R.groupBy(
  (cf: { classificationValues: RankingRuleTriggerConcept[]; mode: string }) => {
    switch (cf.mode) {
      case 'CASCADE':
        return 'cascadingConcepts';
      case 'EXACT_LOCATION':
        return 'exactConcepts';
    }
    return 'na';
  },
);
const groupByKeywordMode = R.groupBy(
  (ky: { searchTerm: string; mode: string }) => {
    switch (ky.mode) {
      case RANKING_RULE_TRIGER_MODE.EXACT:
        return 'exactKeywords';
      case RANKING_RULE_TRIGER_MODE.PARTIAL:
        return 'partialKeywords';
    }
    return 'na';
  },
);
export default function RankingRuleEditFormWrapper({
  data,
}: {
  data: RankingRule;
}) {
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();

  const [triggerModal, setTriggerModal] = useAtom(rankingRuleTriggerModalAtom);
  const [targetModal, setTargetModal] = useAtom(rankingRuleTargetModalAtom);

  const conceptIdsFromQueries = R.flatten(
    data.trigger.queries
      ? data.trigger.queries.classifications.map(
          (cf) => cf.classificationValues,
        )
      : [],
  );
  const conceptIdsFromBuries = data.buries
    ?.filter((val) => val.type === RANKING_RULE_TARGET_TYPES.TAXONOMY)
    .map((b) => b.value);
  const conceptIdsFromBoosts = data.boosts
    ?.filter((val) => val.type === RANKING_RULE_TARGET_TYPES.TAXONOMY)
    .map((b) => b.value);
  const conceptIds = conceptIdsFromQueries
    .concat(conceptIdsFromBoosts as string[])
    .concat(conceptIdsFromBuries as string[]);
  const conceptQueries = conceptIds.map((id) => {
    const queryKey = [QUERY_KEYS.RECOMMEND_CONCEPT_BY_ID, id];
    const queryFn = () => RecommendConceptsClient.getConceptById(id);
    return {
      queryKey,
      queryFn,
      staleTime: Infinity,
      onError: (err: { status: number }) =>
        err.status === 401 && revalidator.revalidate(),
    };
  });
  const conceptData = useQueries({ queries: conceptQueries });
  const isConceptDataLoading = conceptData.some((result) => result.isLoading);

  useEffect(() => {
    if (data.boosts) {
      setTargetModal((modal) => {
        modal.boosts =
          (data.boosts?.map((val) => {
            return val;
          }) as RankingRuleTargetBoostItem[]) || [];
      });
    }
    if (data.buries) {
      setTargetModal((modal) => {
        modal.buries =
          (data.buries?.map((val) => {
            return val;
          }) as RankingRuleTargetBoostItem[]) || [];
      });
    }
    if (!isConceptDataLoading) {
      const concepts = conceptData
        .map((res) => res.data)
        .filter((r) => !!r) as RecommendConcept[];

      const conceptDataById = R.groupBy((con: RecommendConcept) => con.id)(
        concepts,
      ) as Record<string, RecommendConcept[]>;
      const classifications = (data.trigger.queries?.classifications || []).map(
        (cf) => {
          const concepts = cf.classificationValues.map((id) => {
            const data = (conceptDataById[id] && conceptDataById[id][0]) || {
              name: 'unknown',
            };
            return { id, name: data.name };
          });
          return {
            classificationValues: concepts,
            mode: cf.mode,
          };
        },
      );

      const groupedTriggerConcepts = groupByMode(classifications);
      const groupedTriggerKeywords = groupByKeywordMode(
        data.trigger.queries?.keywords || [],
      );
      setTriggerModal((modal) => {
        modal.exactKeywords =
          groupedTriggerKeywords.exactKeywords?.map((ky) => ky.searchTerm) ||
          [];
        modal.partialKeywords =
          groupedTriggerKeywords.partialKeywords?.map((ky) => ky.searchTerm) ||
          [];

        modal.cascadingConcepts =
          groupedTriggerConcepts.cascadingConcepts?.map(
            (c) => c.classificationValues,
          ) || [];
        modal.exactConcepts =
          groupedTriggerConcepts.exactConcepts?.map(
            (c) => c.classificationValues,
          ) || [];
      });

      setTargetModal((modal) => {
        modal.boosts =
          (data.boosts?.map((val) => {
            if (val.type === RANKING_RULE_TARGET_TYPES.TAXONOMY) {
              const id = val.value || 'id';
              const data = (conceptDataById[id] && conceptDataById[id][0]) || {
                name: 'unknown',
              };
              return { label: data.name, ...val };
            }
            return val;
          }) as RankingRuleTargetBoostItem[]) || [];
      });
      setTargetModal((modal) => {
        modal.buries =
          (data.buries?.map((val) => {
            if (val.type === RANKING_RULE_TARGET_TYPES.TAXONOMY) {
              const id = val.value || 'id';
              const data = (conceptDataById[id] && conceptDataById[id][0]) || {
                name: 'unknown',
              };
              return { label: data.name, ...val };
            }
            return val;
          }) as RankingRuleTargetBoostItem[]) || [];
      });
    }
  }, [isConceptDataLoading]);
  if (isConceptDataLoading) {
    return <Loader />;
  }
  return <RankingRuleEditForm data={data} />;
}
